import { useEffect, useRef, useState } from 'react';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface ApiResponse<T> {
  data: T | null;
  error: { message: string } | null;
}

interface UseApiOptions {
  timeoutMs?: number;
}

export function useApi<T>(
  apiCall: () => Promise<ApiResponse<T>> | Promise<T>,
  dependencies: readonly unknown[] = [],
  options: UseApiOptions = {}
): UseApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestRef = useRef(0);

  const fetchData = async () => {
    const requestId = ++requestRef.current;
    const isCurrent = () => mountedRef.current && requestId === requestRef.current;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      if (isCurrent()) setLoading(true);
      if (isCurrent()) setError(null);
      
      // Add timeout handling - configurable per call (default 30s)
      const timeoutMs = options.timeoutMs ?? 30000;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`API call timed out after ${timeoutMs} ms`)), timeoutMs);
      });
      
      const response = await Promise.race([apiCall(), timeoutPromise]);
      clearTimeout(timeoutId);
      
      // Check if response is in ApiResponse format
      if (response && typeof response === 'object' && 'data' in response && 'error' in response) {
        const apiResponse = response as ApiResponse<T>;
        if (apiResponse.error) {
          if (isCurrent()) setError(apiResponse.error.message);
          if (isCurrent()) setData(null);
        } else {
          if (isCurrent()) setData(apiResponse.data);
          if (isCurrent()) setError(null);
        }
      } else {
        // Direct data response
        if (isCurrent()) setData(response as T);
        if (isCurrent()) setError(null);
      }
    } catch (err: any) {
      if (isCurrent()) setError(err.message || 'An unexpected error occurred');
      if (isCurrent()) setData(null);
    } finally {
      clearTimeout(timeoutId);
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, dependencies);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
}

// Specialized hook for API mutations (create, update, delete)
export function useApiMutation<T, P = any>() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = async (
    apiCall: (params: P) => Promise<ApiResponse<T>>,
    params: P
  ): Promise<{ data: T | null; error: string | null }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiCall(params);

      if (response.error) {
        setError(response.error.message);
        return { data: null, error: response.error.message };
      }

      setError(null);
      return { data: response.data, error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    mutate
  };
}

export default useApi;
